/**
 * UserMenu — who is signed in, the Varahi app switcher, Account and Sign out
 * (2026-09-24). Top-right overlay on the dashboard, the same place Happen puts
 * its switcher + avatar. Renders nothing with VITE_AUTH_MODE=off, and nothing
 * while the gate cannot be reached (offline demo), so those look as before.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { Building2, LogIn, LogOut, UserRound } from 'lucide-react';
import { useAuth } from '../../auth/authContext.js';
import { ACCOUNT_URL, AUTH_ENABLED } from '../../auth/config.js';
import { initialsFor, primaryRole } from '../../auth/session.js';
import loadSwitcher from './loadSwitcher.js';
import './UserMenu.css';

// While the menu is on screen, index-level chrome makes room for it (UserMenu.css).
const ROOT_CLASS = 'mhi-auth-menu';

const MODE_LABEL = { email: 'Email access', off: 'Demo access' };

function UserMenu() {
  const { status, user, signOut, signIn } = useAuth();
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const panelId = useId();
  const visible = AUTH_ENABLED && (status === 'ok' || status === 'unauth');
  const signedIn = status === 'ok' && !!user;

  useEffect(() => {
    if (!visible) return undefined;
    const root = document.documentElement;
    root.classList.add(ROOT_CLASS);
    return () => root.classList.remove(ROOT_CLASS);
  }, [visible]);

  useEffect(() => {
    if (signedIn) loadSwitcher();
  }, [signedIn]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!visible) return null;

  if (!signedIn) {
    return (
      <div className="mhi-user" ref={rootRef}>
        <button type="button" className="mhi-user__signin" onClick={signIn}>
          <LogIn size={15} aria-hidden="true" />
          <span>Sign in</span>
        </button>
      </div>
    );
  }

  const role = primaryRole(user.roles);
  const modeLabel = MODE_LABEL[user.mode];
  const display = user.name || user.email || user.sub;

  const onSignOut = async () => {
    setLeaving(true);
    try { await signOut(); } catch { setLeaving(false); }
  };

  return (
    <div className="mhi-user" ref={rootRef}>
      {/* Filled by VarahiID's /id/switcher.js (loadSwitcher), as in Happen's header. */}
      {user.mode === 'varahiid' && <div data-varahi-switcher className="mhi-user__switcher" />}

      <button
        ref={buttonRef}
        type="button"
        className="mhi-user__avatar"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`Account menu for ${display}`}
        title={user.email || display}
        onClick={() => setOpen((v) => !v)}
      >
        {initialsFor(user)}
      </button>

      {open && (
        <div className="mhi-user__panel" id={panelId} role="menu" aria-label="Account">
          <div className="mhi-user__who">
            <p className="mhi-user__name">{display}</p>
            {user.name && user.email && <p className="mhi-user__email">{user.email}</p>}
            {user.tslug && (
              <p className="mhi-user__tenant">
                <Building2 size={12} aria-hidden="true" />
                <span>{user.tslug}</span>
              </p>
            )}
            {(role || modeLabel) && (
              <div className="mhi-user__chips">
                {role && <span className="mhi-user__chip">{role}</span>}
                {modeLabel && <span className="mhi-user__chip mhi-user__chip--mode">{modeLabel}</span>}
              </div>
            )}
          </div>

          {user.mode === 'varahiid' && (
            <a className="mhi-user__item" role="menuitem" href={ACCOUNT_URL}>
              <UserRound size={15} aria-hidden="true" />
              <span>Account</span>
            </a>
          )}
          <button
            type="button"
            className="mhi-user__item"
            role="menuitem"
            onClick={onSignOut}
            disabled={leaving}
          >
            <LogOut size={15} aria-hidden="true" />
            <span>{leaving ? 'Signing out…' : 'Sign out'}</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default React.memo(UserMenu);
